import { FC, ReactNode } from 'react'

import * as LabelPrimitive from '@radix-ui/react-label'
import { styled } from 'styled-components'

import Tooltip from 'core/components/lib/Tooltip'
import { useIsOverflowing } from 'core/hooks'
import { onSurface, onSurfaceVariant, outline } from 'core/styles'

type FormLabelProps = {
  text: ReactNode
  htmlFor?: string
  className?: string
  trailingContent?: ReactNode
  tooltipContent?: ReactNode
  isRequired?: boolean
}

export const Label: FC<FormLabelProps> = ({
  text,
  htmlFor,
  className,
  trailingContent,
  tooltipContent,
  isRequired,
}) => (
  <LabelContainer className={className} htmlFor={htmlFor}>
    <Row>
      <LabelText>
        {text}
        {isRequired ?
          <RequiredIndicator>*</RequiredIndicator>
        : null}
      </LabelText>{' '}
      {!!tooltipContent && <Tooltip content={<TooltipContent>{tooltipContent}</TooltipContent>} iconSize='16px' />}
    </Row>
    {trailingContent == null ? null : (
      <LabelText>
        <TrailingContent>{trailingContent}</TrailingContent>
      </LabelText>
    )}
  </LabelContainer>
)

export type FieldValueProps = {
  $variant?: 'default' | 'compact' | 'multiline'
  children?: ReactNode
  className?: string
}

export const FieldValue: FC<FieldValueProps> = ({ $variant, children, className }) => (
  <FieldValueBox $variant={$variant} className={className}>
    {$variant === 'compact' ?
      <FieldValueTruncated>{children}</FieldValueTruncated>
    : children}
  </FieldValueBox>
)

/** Truncates a long value with an ellipsis, revealing the full text in a Tooltip only when it overflows. */
export const FieldRowTruncatedValue: FC<{ children?: ReactNode; className?: string }> = ({ children, className }) => {
  const [ref, isOverflowing] = useIsOverflowing<HTMLDivElement>([children])
  return (
    <FieldRowTruncatedValueWrapper className={className}>
      <Tooltip anchor='text' animation='shift-away' content={children} disabled={!isOverflowing} placement='top'>
        <FieldRowTruncatedValueText ref={ref}>{children}</FieldRowTruncatedValueText>
      </Tooltip>
    </FieldRowTruncatedValueWrapper>
  )
}

export const FormContainer = styled.form`
  display: flex;
  flex-flow: row wrap;
  gap: 24px;

  > * {
    flex: 1 1 100%;
    min-width: 0;
  }
`

export const FormRow = styled.div<{ $variant?: 'horizontal' | 'vertical'; $gap?: string }>`
  display: flex;
  flex-direction: ${(p) => (p.$variant === 'horizontal' ? 'row' : 'column')};
  gap: ${(p) => p.$gap ?? '0'};
  align-items: ${(p) => (p.$variant === 'horizontal' ? 'start' : 'stretch')};
  justify-content: ${(p) => (p.$variant === 'horizontal' ? 'space-between' : 'flex-start')};
  width: 100%;

  ${(p) =>
    p.$variant === 'vertical' &&
    `
      > * {
        width: 100%;
      }
    `}

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const LabelContainer = styled(LabelPrimitive.Root)`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-bottom: 2px;
`

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

export const LabelText = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  color: ${onSurfaceVariant};
  font-size: 13px;
  font-weight: 400;
`

const TooltipContent = styled.div`
  font-size: 14px;
`

const TrailingContent = styled.div`
  display: inline-flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  opacity: 0.7;
  margin-left: 8px;
  color: ${onSurfaceVariant};
  font-size: 11px;
  font-weight: 600;
`

export const FormValue = styled.span<{ $variant?: 'default' | 'notification' | 'light' }>`
  font-size: 14px;
  ${(p) =>
    p.$variant === 'notification' ?
      `
      color: ${onSurfaceVariant};
      font-weight: 500;
    `
    : p.$variant === 'light' ?
      `
      opacity: 0.8;
      color: ${onSurfaceVariant};
      font-weight: 400;
    `
    : `
      color: ${onSurface};
      font-weight: 600;
    `}
`

export const FieldLabel = styled.label<{ $variant?: 'default' | 'light' }>`
  display: block;
  padding-bottom: 2px;
  color: ${onSurface};
  font-size: 13px;
  font-weight: 500;

  ${(p) =>
    p.$variant === 'light' &&
    `
      font-size: 14px;
      font-weight: 400;
    `}
`

export const ContactFieldRow = styled(FormRow)`
  box-sizing: border-box;
  margin-bottom: 8px;
  min-width: 0;
`

export const FieldRowActions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  margin-left: auto;
`

/** Height matches compact TextInput/buttons, so the row height doesn't shift between read and edit states. */
export const FieldRowValue = styled.div`
  display: flex;
  align-items: center;
  height: 32px;
  color: ${onSurface};
`

const RequiredIndicator = styled.span`
  margin-left: 4px;
`

const FieldValueBox = styled.div<{ $variant?: 'default' | 'compact' | 'multiline' }>`
  box-sizing: border-box;
  border: 1px solid ${outline};
  border-radius: 8px;
  color: ${onSurface};
  font-size: 14px;

  ${(p) =>
    p.$variant === 'compact' ?
      `
    padding: 0 12px;
    height: 32px;
    font-weight: 500;
    display: flex;
    align-items: center;
    min-width: 0;

    &:hover {
      border-color: ${outline};
    }
  `
    : p.$variant === 'multiline' ?
      `
    padding: 6px 12px;
    min-height: 32px;
    white-space: pre-wrap;
    word-break: break-all;
  `
    : `
    padding: 10px 12px;
  `}
`

/** Block child so `text-overflow: ellipsis` applies (not reliable on the flex container itself). */
const FieldValueTruncated = styled.span`
  display: block;
  flex: 1 1 0;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const FieldRowTruncatedValueWrapper = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  min-width: 0;
  height: 32px;
  overflow: hidden;
`

const FieldRowTruncatedValueText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
